"use client";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { updateCart } from "../services";
import { CartSchema } from "../types";
import { useStoreContext } from "@/lib/store";
import { getErrorMessage } from "@/lib";
import { updateOutlet } from "@/app/(hub)/settings/outlets/services";

const cartShippingSchema = z.object({
  shipmentMethod: z.string().min(1, { message: "Shipment method is required" }),
  deliveryAddress: z
    .string()
    .min(1, { message: "Delivery address is required" }),
});

export function useCartPanel(
  cartId: string,
  cart: CartSchema | undefined,
  mutateCart?: () => void,
  handleNext?: () => void,
) {
  const [loading, setLoading] = useState(false);
  const { outlet, outlets } = useStoreContext();
  const methods = useForm<{
    shipmentMethod: string;
    deliveryAddress: string;
  }>({
    resolver: zodResolver(cartShippingSchema),
  });
  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    getValues,
    clearErrors,
  } = methods;
  const { errors } = formState;

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    const res = await updateCart(cartId, data);
    if (res?.error) {
      toast.error(getErrorMessage(res));
    } else {
      if (mutateCart) mutateCart();
      if (handleNext) handleNext();
      setLoading(false);
    }
  });

  async function saveAddress(address: string) {
    toast.loading("Saving...");
    const [cartResponse, outletResponse] = await Promise.all([
      updateCart(cartId, { deliveryAddress: address }),
      updateOutlet(outlet, {
        name: outlets[0]?.name,
        address,
      }),
    ]);
    if (cartResponse?.error || outletResponse?.error) {
      toast.dismiss();
      toast.error(
        getErrorMessage(cartResponse?.error ? cartResponse : outletResponse),
      );
    } else {
      toast.dismiss();
      toast.success("Address successfully saved!");
    }
  }

  useEffect(() => {
    if (cart || outlets?.length > 0) {
      setValue("shipmentMethod", cart?.shipmentMethod || "");
      setValue(
        "deliveryAddress",
        cart?.deliveryAddress ?? outlets?.[0]?.address ?? "",
      );
    }
  }, [cart, outlets]);

  return {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    onSubmit,
    loading,
    errors,
    clearErrors,
    getValues,
    saveAddress,
  };
}
